import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, HardHat, MapPin } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

type Proyecto = {
  id: string; folio: string; nombre: string; tipo: string; estado: string;
  presupuesto_autorizado: number; monto_contratado: number; monto_ejecutado: number;
  fecha_inicio_programada?: string; fecha_fin_programada?: string;
  localidad?: string; municipio?: string; fuente_financiamiento?: string;
};

export function ObrasPage() {
  const [estado, setEstado] = useState('');
  const [openNew, setOpenNew] = useState(false);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['obras', estado],
    queryFn: () => {
      const p = new URLSearchParams();
      if (estado) p.set('estado', estado);
      return api<Proyecto[]>(`/obra/proyectos?${p}`);
    },
  });
  const items = data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Obra Pública · Proyectos</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Proyectos de obra</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            Infraestructura, urbanización, vialidad, hidráulica y más. Estimaciones mensuales con retenciones 5‰ SFP + 2‰ CMIC + IVA.
          </p>
        </div>
        <Button onClick={() => setOpenNew(true)}><Plus size={16} /> Nuevo proyecto</Button>
      </div>

      <div className="flex items-center gap-1 rounded-md border border-border bg-surface p-0.5 text-[12px] font-medium w-fit overflow-x-auto">
        {['', 'planeacion', 'licitacion', 'contratado', 'en_ejecucion', 'suspendido', 'finiquitado', 'cancelado'].map(e => (
          <button key={e || 'todas'} onClick={() => setEstado(e)}
            className={cn('whitespace-nowrap rounded px-3 py-1.5 capitalize', estado === e ? 'bg-brand-500 text-white' : 'text-ink-mute hover:bg-muted')}>
            {e ? e.replace(/_/g, ' ') : 'todas'}
          </button>
        ))}
      </div>

      <Card>
        <CardBody className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <HardHat className="h-7 w-7 text-ink-soft" />
              <p className="text-[13px] text-ink-mute">Sin proyectos de obra.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 lg:grid-cols-3">
              {items.map(p => {
                const avance = p.monto_contratado > 0 ? (p.monto_ejecutado * 100 / p.monto_contratado) : 0;
                return (
                  <Link key={p.id} to={`/app/obras/${p.id}`}
                    className="group rounded-lg border border-border bg-surface p-4 shadow-subtle transition hover:shadow-card">
                    <div className="flex items-start justify-between">
                      <div className="num text-[11px] font-semibold text-brand-700">{p.folio}</div>
                      <EstadoPill e={p.estado} />
                    </div>
                    <h3 className="mt-2 font-display text-[15px] font-semibold leading-tight text-ink line-clamp-2">{p.nombre}</h3>
                    {(p.localidad || p.municipio) && (
                      <p className="mt-1 flex items-center gap-1 text-[11px] text-ink-mute">
                        <MapPin size={10} /> {[p.localidad, p.municipio].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    <div className="mt-3 flex items-center justify-between text-[11px] text-ink-mute">
                      <span>Autorizado</span>
                      <span className="num font-semibold text-ink">{fmt.money(p.presupuesto_autorizado)}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[11px] text-ink-mute">
                      <span>Contratado</span>
                      <span className="num font-semibold">{fmt.money(p.monto_contratado)}</span>
                    </div>
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-ink-mute">Avance financiero</span>
                        <span className="num font-semibold text-ink">{avance.toFixed(1)}%</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                        <div className="h-full bg-brand-500" style={{ width: `${Math.min(100, avance)}%` }} />
                      </div>
                    </div>
                    {p.fuente_financiamiento && (
                      <div className="mt-2 text-[11px] text-ink-mute">{p.fuente_financiamiento}</div>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>

      <NuevoProyectoModal open={openNew} onClose={() => setOpenNew(false)}
        onCreated={() => { setOpenNew(false); qc.invalidateQueries({ queryKey: ['obras'] }); }} />
    </div>
  );
}

function EstadoPill({ e }: { e: string }) {
  const cfg: Record<string, string> = {
    planeacion: 'bg-stone-100 text-ink-mute',
    licitacion: 'bg-sky-50 text-sky-700',
    contratado: 'bg-amber-50 text-amber-700',
    en_ejecucion: 'bg-brand-50 text-brand-700',
    suspendido: 'bg-rose-50 text-rose-700',
    finiquitado: 'bg-emerald-50 text-emerald-700',
    cancelado: 'bg-stone-100 text-ink-soft',
  };
  return <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium', cfg[e] ?? 'bg-muted')}>{e.replace(/_/g, ' ')}</span>;
}

function NuevoProyectoModal({ open, onClose, onCreated }: any) {
  const [v, setV] = useState({
    nombre: '', tipo: 'infraestructura', descripcion: '',
    localidad: '', municipio: '', estado_geografico: '',
    presupuesto_autorizado: 0, fuente_financiamiento: '',
    beneficiarios: 0,
    fecha_inicio_programada: '', fecha_fin_programada: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      await api('/obra/proyectos', {
        method: 'POST',
        body: {
          ...v,
          presupuesto_autorizado: Number(v.presupuesto_autorizado),
          beneficiarios: Number(v.beneficiarios) || undefined,
          fecha_inicio_programada: v.fecha_inicio_programada ? new Date(v.fecha_inicio_programada).toISOString() : undefined,
          fecha_fin_programada: v.fecha_fin_programada ? new Date(v.fecha_fin_programada).toISOString() : undefined,
        },
      });
      onCreated();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Nuevo proyecto de obra" size="xl"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="obra-form" loading={subm}>Crear proyecto</Button>
      </>}>
      <form id="obra-form" onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {err && <div className="sm:col-span-2 rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}
        <div className="sm:col-span-2">
          <Input name="nombre" label="Nombre del proyecto" required
            value={v.nombre} onChange={(e) => setV({ ...v, nombre: e.target.value })} />
        </div>
        <div>
          <label className="text-[13px] font-medium">Tipo</label>
          <select value={v.tipo} onChange={(e) => setV({ ...v, tipo: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="infraestructura">Infraestructura</option>
            <option value="edificacion">Edificación</option>
            <option value="urbanizacion">Urbanización</option>
            <option value="vialidad">Vialidad</option>
            <option value="hidraulica">Hidráulica</option>
            <option value="electrificacion">Electrificación</option>
            <option value="otra">Otra</option>
          </select>
        </div>
        <Input name="fuente" label="Fuente financiamiento" hint="FAIS, FORTAMUN, recursos propios…"
          value={v.fuente_financiamiento} onChange={(e) => setV({ ...v, fuente_financiamiento: e.target.value })} />
        <div className="sm:col-span-2">
          <label className="text-[13px] font-medium">Descripción</label>
          <textarea rows={2} value={v.descripcion} onChange={(e) => setV({ ...v, descripcion: e.target.value })}
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px]" />
        </div>
        <Input name="loc" label="Localidad" value={v.localidad} onChange={(e) => setV({ ...v, localidad: e.target.value })} />
        <Input name="mun" label="Municipio" value={v.municipio} onChange={(e) => setV({ ...v, municipio: e.target.value })} />
        <Input name="presup" label="Presupuesto autorizado" type="number" step="0.01" required
          value={v.presupuesto_autorizado} onChange={(e) => setV({ ...v, presupuesto_autorizado: parseFloat(e.target.value) || 0 })} />
        <Input name="benef" label="Beneficiarios" type="number"
          value={v.beneficiarios} onChange={(e) => setV({ ...v, beneficiarios: parseInt(e.target.value) || 0 })} />
        <Input name="ini" label="Inicio programado" type="date"
          value={v.fecha_inicio_programada} onChange={(e) => setV({ ...v, fecha_inicio_programada: e.target.value })} />
        <Input name="fin" label="Fin programado" type="date"
          value={v.fecha_fin_programada} onChange={(e) => setV({ ...v, fecha_fin_programada: e.target.value })} />
      </form>
    </Modal>
  );
}
